import type { CalendarBooking, CalendarUnit, CalendarAccommodation, ContractDraft } from './types';
import { fmtPretty, daysBetween } from './pricing';

let _units: CalendarUnit[] = [];
let _accoms: CalendarAccommodation[] = [];
let _i18n: Record<string, string> = {};
let _monthNames: string[] = [];
let _contractCreateUrl = '';
let _contractDetailsUrl = '';
let _currency = '';
let _activeBookingModal: HTMLElement | null = null;
let _activeContractModal: HTMLElement | null = null;

export function initModals(
    units: CalendarUnit[],
    accoms: CalendarAccommodation[],
    i18n: Record<string, string>,
    monthNames: string[],
    contractCreateUrl: string,
    contractDetailsUrl: string,
    currency: string
): void {
    _units = units;
    _accoms = accoms;
    _i18n = i18n;
    _monthNames = monthNames;
    _contractCreateUrl = contractCreateUrl;
    _contractDetailsUrl = contractDetailsUrl;
    _currency = currency;
}

// ── Booking detail modal ──────────────────────────────────────────────────

export function openBookingModal(booking: CalendarBooking): void {
    closeAll();
    const unit = _units.find(u => u.id === booking.unitId);
    const accom = unit ? _accoms.find(a => a.id === unit.accomId) : null;
    const nights = daysBetween(booking.from, booking.to);
    const dotColor = booking.status === 'option' ? 'var(--cal-option)' : 'var(--cal-booked)';
    const statusLabel = booking.status === 'option' ? _i18n['optioned'] : _i18n['booked'];
    const contractUrl = `${_contractDetailsUrl}/${booking.contractId}`;

    const el = createBackdrop();
    el.innerHTML = `
      <div class="cal-modal" role="dialog" aria-modal="true">
        <div class="cal-modal-header">
          <div class="cal-modal-title">
            <span class="booking-dot" style="background:${dotColor}"></span>
            ${esc(String(booking.contractId))}
            <span style="font-weight:600;color:var(--pk-navy-muted,#7a849b);font-size:13px">${esc(statusLabel)}</span>
          </div>
          <button class="cal-modal-close" aria-label="Close">✕</button>
        </div>
        <div class="cal-modal-body">
          ${kv(_i18n['guest'] || 'Guest', esc(booking.guest))}
          ${kv(_i18n['accommodation'] || 'Accommodation', esc(accom?.name ?? '—'))}
          ${kv(_i18n['unitAccom']?.split('/')?.pop()?.trim() ?? 'Unit', esc(`${unit?.name ?? '—'} · ${unit?.type ?? ''}`))}
          ${kv(_i18n['checkIn'] || 'Check-in', fmtPretty(booking.from, _monthNames))}
          ${kv(_i18n['checkOut'] || 'Check-out', fmtPretty(booking.to, _monthNames))}
          ${kv(_i18n['nights'] || 'Nights', String(nights))}
          ${kv(_i18n['pax'] || 'Pax', String(booking.pax))}
        </div>
        <div class="cal-modal-footer">
          <button class="btn btn-secondary btn-sm js-close">${esc(_i18n['close'] || 'Close')}</button>
          <a class="btn btn-primary btn-sm" href="${contractUrl}">${esc(_i18n['openContract'] || 'Open contract →')}</a>
        </div>
      </div>`;

    wireClose(el);
    document.body.appendChild(el);
    _activeBookingModal = el;
}

// ── Contract preview modal ────────────────────────────────────────────────

export function openContractModal(draft: ContractDraft): void {
    closeAll();
    const unit = _units.find(u => u.id === draft.unitId);
    const accom = unit ? _accoms.find(a => a.id === unit.accomId) : null;
    const avg = draft.nights > 0 ? Math.round(draft.total / draft.nights) : 0;
    const breakdownRows = draft.prices.map(p =>
        `<span class="breakdown-date">${fmtPretty(p.date, _monthNames)}</span>` +
        `<span class="breakdown-price">${p.price != null ? `${p.price} ${_currency}` : '—'}</span>`
    ).join('');

    const nightLabel = draft.nights === 1
        ? (_i18n['night'] || 'night')
        : (_i18n['nights'] || 'nights');

    const prefillUrl = `${_contractCreateUrl}?offerUnitId=${draft.unitId}&checkIn=${encodeURIComponent(draft.from)}&checkOut=${encodeURIComponent(draft.to)}`;

    const el = createBackdrop();
    el.innerHTML = `
      <div class="cal-modal" role="dialog" aria-modal="true">
        <div class="cal-modal-header">
          <div class="cal-modal-title">${esc(_i18n['createContractFrom'] || 'Create contract from selection')}</div>
          <button class="cal-modal-close" aria-label="Close">✕</button>
        </div>
        <div class="cal-modal-body">
          <div class="price-summary">
            <div class="price-summary-meta">${esc(_i18n['total'] || 'Total')} · ${draft.nights} ${esc(nightLabel)}</div>
            <div class="price-summary-total">${draft.total.toLocaleString()} ${_currency}</div>
            <div style="font-size:12px;color:var(--bs-primary);font-weight:700;margin-top:4px">
              ${esc(_i18n['avgPerNight'] ? `avg ${avg} ${_currency} / ${_i18n['night'] || 'night'}` : `avg ${avg} ${_currency} / night`)}
            </div>
          </div>
          ${kv(_i18n['accommodation'] || 'Accommodation', esc(accom?.name ?? '—'))}
          ${kv('Unit', esc(`${unit?.name ?? '—'} · ${unit?.type ?? ''}`))}
          ${kv(_i18n['service'] || 'Service', esc(`${unit?.service ?? ''} · ${unit?.classification ?? ''}`))}
          ${kv(_i18n['checkIn'] || 'Check-in', fmtPretty(draft.from, _monthNames))}
          ${kv(_i18n['checkOut'] || 'Check-out', fmtPretty(draft.to, _monthNames))}
          ${kv(_i18n['nights'] || 'Nights', String(draft.nights))}

          <div class="breakdown-label">${esc(_i18n['perNightBreakdown'] || 'Per-night breakdown')}</div>
          <div class="breakdown-grid">${breakdownRows}</div>
        </div>
        <div class="cal-modal-footer">
          <button class="btn btn-secondary btn-sm js-close">${esc(_i18n['cancel'] || 'Cancel')}</button>
          <a class="btn btn-secondary btn-sm" href="${prefillUrl}" target="_blank" rel="noopener noreferrer">${esc(_i18n['openContractForm'] || 'Open contract form')}</a>
          <a class="btn btn-primary btn-sm" href="${prefillUrl}">${esc(_i18n['createContract'] || 'Create contract')} →</a>
        </div>
      </div>`;

    wireClose(el);
    document.body.appendChild(el);
    _activeContractModal = el;
}

export function closeAll(): void {
    _activeBookingModal?.remove();
    _activeContractModal?.remove();
    _activeBookingModal = null;
    _activeContractModal = null;
}

// ── Helpers ───────────────────────────────────────────────────────────────

function createBackdrop(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'availability-calendar cal-modal-backdrop';
    el.addEventListener('click', e => { if (e.target === el) closeAll(); });
    return el;
}

function wireClose(el: HTMLElement): void {
    el.querySelector('.cal-modal-close')?.addEventListener('click', closeAll);
    el.querySelector('.js-close')?.addEventListener('click', closeAll);
}

function kv(label: string, value: string): string {
    return `<div class="kv-row"><span class="k">${label}</span><span class="v">${value}</span></div>`;
}

function esc(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
